// A Reports number card (Figma "KPI · …", 11521:96836; 044 D77, D85, plan slice 8 R10): the
// label, the number, the change chip and the second line, and the trend line under them.
import React from "react";
import { Box, Typography } from "@mui/material";
import { RiArrowRightDownLine, RiArrowRightLine, RiArrowRightUpLine } from "@remixicon/react";
import { srOnly, tokens } from "../../utils/figmaTokens";
import { Sparkline } from "./Charts";

// R6: up is good for all four numbers, so up is green.
const TONES = {
  up: { ...tokens.success, Icon: RiArrowRightUpLine },
  down: { ...tokens.error, Icon: RiArrowRightDownLine },
  flat: { ...tokens.neutral, Icon: RiArrowRightLine },
};

// D85: the trend line is green when the chip is up, red when it is down, grey otherwise.
const LINES = { up: tokens.success.main, down: tokens.error.main };

// A change chip (D78): reportNumbers' { tone, text, before } — green up, red down, grey level.
// The Reports cards and the subject chart (044 D128) draw it.
export const ChangeChip = ({ chip }) => {
  const tone = TONES[chip.tone];
  return (
    <Box
      component="span"
      title={`Compared with ${chip.before}`}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        height: 26,
        px: "10px",
        borderRadius: "6px",
        bgcolor: tone.bg,
        color: tone.ink,
        flexShrink: 0,
      }}
    >
      <tone.Icon size={16} aria-hidden />
      <Typography component="span" variant="labelMedium" sx={{ color: "inherit", whiteSpace: "nowrap" }}>
        {chip.text}
      </Typography>
      <Box component="span" sx={srOnly}>{` compared with ${chip.before}`}</Box>
    </Box>
  );
};

// `chip` is reportNumbers' { tone, text, before } or null; `value` and `caption` may be nodes
// (the page's loading skeletons); `trend` is the trend line's points, or null for none.
const KpiCard = ({ label, value, chip, caption, trend }) => {
  return (
    <Box
      sx={{
        bgcolor: tokens.surface.default,
        border: `1px solid ${tokens.border.default}`,
        borderRadius: "16px",
        p: "16px",
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        minWidth: 0,
      }}
    >
      <Typography variant="bodySmall" sx={{ color: tokens.text.secondary }}>
        {label}
      </Typography>
      <Typography
        component="div"
        sx={{ fontSize: 28, lineHeight: "32px", fontWeight: 600, letterSpacing: "-0.8px", color: tokens.text.primary }}
      >
        {value}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: "8px", minWidth: 0 }}>
        {chip && <ChangeChip chip={chip} />}
        <Typography component="div" variant="bodySmall" sx={{ color: tokens.text.secondary, minWidth: 0 }}>
          {caption}
        </Typography>
      </Box>
      {/* The card is a flex column that fills its grid row, so the line sits on the card's bottom edge
          and all four cards' lines line up, whatever their captions wrap to (owner, 2026-09-30). */}
      {trend && trend.length > 1 && (
        <Box sx={{ mt: "auto" }}>
          <Sparkline points={trend} color={LINES[chip?.tone] || tokens.neutral.ink} />
        </Box>
      )}
    </Box>
  );
};

export default KpiCard;
